import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckSquare, Wallet, ChevronRight } from "lucide-react";
import { Card, CardContent, Badge } from "@tabipla/ui";
import type { TripResponseDto } from "@tabipla/shared";
import { usePackingProgress } from "@/application/hooks/usePacking";
import { useBalanceSummary } from "@/application/hooks/useExpenses";
import { computeTripStatus } from "@tabipla/shared";

interface NextStepsPanelProps {
  trips: TripResponseDto[];
}

interface NextStepItem {
  id: string;
  type: "packing" | "settlement";
  tripId?: string;
  tripTitle?: string;
  title: string;
  description: string;
  progress?: number;
  icon: typeof CheckSquare;
  color: string;
  href: string;
}

/**
 * Find the next upcoming trip that needs attention
 */
function useNextUpcomingTrip(trips: TripResponseDto[]) {
  return useMemo(() => {
    const upcomingTrips = trips
      .filter((trip) => {
        const status = computeTripStatus(trip.startDate, trip.endDate);
        return status === "planning" || status === "confirmed";
      })
      .sort((a, b) => {
        // Sort by start date, trips with dates first
        if (!a.startDate && !b.startDate) return 0;
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
      });

    return upcomingTrips[0] ?? null;
  }, [trips]);
}

export function NextStepsPanel({ trips }: NextStepsPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get the next upcoming trip
  const nextTrip = useNextUpcomingTrip(trips);

  // Get packing progress for the next trip
  const { data: packingProgress } = usePackingProgress(nextTrip?.id ?? "");

  // Get balance summary
  const { data: balanceSummary } = useBalanceSummary();

  // Build the list of next steps
  const nextSteps = useMemo(() => {
    const steps: NextStepItem[] = [];

    // Check packing progress for next trip
    if (nextTrip) {
      if (packingProgress && packingProgress.total > 0 && packingProgress.percentage < 100) {
        steps.push({
          id: `packing-${nextTrip.id}`,
          type: "packing",
          tripId: nextTrip.id,
          tripTitle: nextTrip.title,
          title: t("home.nextSteps.packing", "持ち物を準備"),
          description: t("home.nextSteps.packingProgress", "{{checked}}/{{total}}個チェック済み", {
            checked: packingProgress.checked,
            total: packingProgress.total,
          }),
          progress: packingProgress.percentage,
          icon: CheckSquare,
          color: "text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900",
          href: `/trips/${nextTrip.id}?tab=packing`,
        });
      }
    }

    // Check for unsettled balances
    if (balanceSummary && balanceSummary.netBalance !== 0) {
      const isReceivable = balanceSummary.netBalance > 0;
      steps.push({
        id: "settlement",
        type: "settlement",
        title: t("home.nextSteps.settlement", "精算を確認"),
        description: isReceivable
          ? t("home.nextSteps.receivable", "{{amount}}を受け取れます", {
              amount: new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(
                balanceSummary.netBalance
              ),
            })
          : t("home.nextSteps.payable", "{{amount}}を支払う必要があります", {
              amount: new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(
                Math.abs(balanceSummary.netBalance)
              ),
            }),
        icon: Wallet,
        color: isReceivable
          ? "text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900"
          : "text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900",
        href: "/groups",
      });
    }

    return steps;
  }, [nextTrip, packingProgress, balanceSummary, t]);

  // Don't show if no next steps
  if (nextSteps.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{t("home.nextSteps.title", "やることリスト")}</h2>
      <Card>
        <CardContent className="divide-y p-0">
          {nextSteps.map((step) => (
            <button
              key={step.id}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent"
              onClick={() => navigate(step.href)}
            >
              {/* Icon */}
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${step.color}`}
              >
                <step.icon className="h-5 w-5" />
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{step.title}</span>
                  {step.tripTitle && (
                    <Badge variant="secondary" className="text-xs">
                      {step.tripTitle}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{step.description}</p>
                {step.progress !== undefined && (
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-blue-500 transition-all"
                      style={{ width: `${step.progress}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Arrow */}
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </CardContent>
      </Card>
    </section>
  );
}
